import dayjs from 'dayjs';
import type { EditReservationTimeslotsInDTO, ReservationDetailOutDTO } from '@api/ringil-api.v2.tsx';
import type { Order } from '@components/framework/orders/common.tsx';
import {
  apiPragueFromDayjsLocal,
  apiUtcFromDayjs,
  dayjsFromApiUtcInTz,
  dayjsFromDayAndTimeStrings,
  stringHHmmFromStringInTz,
  stringYMDFromDayjs,
} from '@utils/dateWithTz.tsx';
import { isNotNullOrUndefined } from '@utils/utils.tsx';

/**
 * RD-3062 — the form ↔ API mapping of `EditReservationSlot`, moved out of the component unchanged so it
 * can be unit-tested. The component keeps the hooks, API calls and UI; everything here is pure.
 */

export interface EditReservationSlotForm {
  companyLocationId: string;
  rampId: string;
  localDate: string;
  preferredDate?: string;
  durationInMin: string;
  arrivalHourString: string;
  timezoneId: string;
}

/** The reservation's orders, as the external-orders date range reads them (names and external data only). */
export const reservationOrdersFromDetail = (d: ReservationDetailOutDTO): Order[] =>
  (d.orders ?? []).map(o => ({
    orderName: o.orderName,
    items: [],
    externalOrders: (o.externalData ?? []).map(ed => ({
      orderNumber: ed.orderNumber,
      timeFrom: ed.timeFrom,
      timeTo: ed.timeTo,
    })),
  }));

/**
 * The form a loaded reservation starts from, and the values it is compared against to skip re-checking an
 * unchanged slot. Slots with UTC fields are read in their own timezone; older ones fall back to Prague.
 */
export const editReservationSlotFormFromDetail = (
  d: ReservationDetailOutDTO,
): { formValues: EditReservationSlotForm; originalValues: Omit<EditReservationSlotForm, 'companyLocationId'> } => {
  const hasNewFields = isNotNullOrUndefined(d.slot?.utcStart) && isNotNullOrUndefined(d.slot?.timezoneId);
  const timezoneId = hasNewFields ? d.slot?.timezoneId : 'Europe/Prague';
  const localDate = hasNewFields
    ? stringYMDFromDayjs(dayjsFromApiUtcInTz(d.slot?.utcStart, d.slot?.timezoneId))
    : (d.slot?.localDate ?? d.preferredDate);
  const arrivalHourString = hasNewFields
    ? stringHHmmFromStringInTz(d.slot?.utcStart, d.slot?.timezoneId)
    : (d.slot?.sinceHourString ?? '');
  const finalLocalDate = localDate ?? d.preferredDate ?? dayjs().format('YYYY-MM-DD');

  return {
    formValues: {
      localDate: finalLocalDate,
      rampId: d.rampId,
      arrivalHourString: arrivalHourString ?? '',
      durationInMin: d.slot?.durationInMin ? `${Math.floor(d.slot?.durationInMin)}` : '30',
      companyLocationId: d.locationId,
      preferredDate: d.preferredDate,
      timezoneId,
    },
    originalValues: {
      localDate: finalLocalDate,
      rampId: d.rampId,
      arrivalHourString: arrivalHourString ?? '',
      durationInMin: d.slot?.durationInMin ? `${Math.floor(d.slot?.durationInMin)}` : '30',
      timezoneId,
    },
  };
};

/** The body of `updateReservationTimeslot` — legacy fields only. */
export const updateReservationTimeslotRequest = (data: EditReservationSlotForm): EditReservationTimeslotsInDTO => {
  const durationMinutes = parseInt(data.durationInMin);

  return {
    // Legacy fields (still required for backward compatibility)
    rampId: data.rampId,
    localDate: data.localDate,
    sinceHourString: data.arrivalHourString,
    durationInMinutes: durationMinutes,
  };
};

/**
 * The timeslot the availability check sends for an edited slot: the wall clock read as Prague time for the
 * legacy fields, and in the slot's own timezone for the UTC ones.
 * `durationInMin` is the form's string; dayjs `add` has always converted it itself, hence the cast.
 */
export const editSlotTimeslotForReservationCheck = (debouncedValue: Omit<EditReservationSlotForm, 'companyLocationId'>) => ({
  // Legacy fields (Europe/Prague timezone)
  since: apiPragueFromDayjsLocal(dayjsFromDayAndTimeStrings(debouncedValue.localDate, debouncedValue.arrivalHourString, 'Europe/Prague')),
  till: apiPragueFromDayjsLocal(
    dayjsFromDayAndTimeStrings(debouncedValue.localDate, debouncedValue.arrivalHourString, 'Europe/Prague').add(debouncedValue.durationInMin as unknown as number, 'minute'),
  ),
  // New UTC fields
  utcStart: apiUtcFromDayjs(dayjsFromDayAndTimeStrings(debouncedValue.localDate, debouncedValue.arrivalHourString, debouncedValue.timezoneId)),
  utcEnd: apiUtcFromDayjs(
    dayjsFromDayAndTimeStrings(debouncedValue.localDate, debouncedValue.arrivalHourString, debouncedValue.timezoneId).add(debouncedValue.durationInMin as unknown as number, 'minute'),
  ),
  timezoneId: debouncedValue.timezoneId,
});
